import { Maybe } from 'graphql/jsutils/Maybe';
import Head from 'next/head';
import { useRouter } from 'next/router';

import {
  Episode,
  TvShowRelatedContent
} from 'src/services/GraphQL/Queries/types';
import { getCookie, getLocalStorage } from 'src/utils/browserStorageHandler';
import {
  assetContentMapping,
  gadar2MovieID,
  ghoomerMovieId,
  kadakSinghMovieId, // SPORTS_ROUTE
  Khichdi2MovieId,
  mainAtalHoonMovieId,
  samBahadurMovieId,
  tejasMovieId,
  theKeralaStoryMovieId
} from 'src/utils/constants/CanonicalURLConstants';
import { DISPLAY_LANGUAGE_COOKIE } from 'src/utils/constants/cookieConstants';
import { SETTINGS } from 'src/utils/constants/localStorageConstants';
import {
  CountryCode,
  DisplayLanguage
} from 'src/utils/constants/stringConstants';
import { createOgTags, getEpisodeIndex } from 'src/utils/OGTags';

import { useAppStore } from 'src/hooks/useAppStore';
import { CelebsMetaData } from 'src/services/responseTypes/celebsResponse';
import { ContentLiteData } from '../../types/consumption';
import { DynamicObject } from '../../utils/commonTypes';
import {
  KISI_KA_BHAI_KISI_KI_JAAN_CONTENT_ID,
  RRR_CONTENT_ID,
  SIRF_EK_BANDAA_KAAFI_HAI_CONTENT_ID,
  WANTED_CONTENT_ID,
  WRITER_PADMABUSHAN_CONTENT_ID
} from '../../utils/constants/SEOMetaConstants';
import {
  BASE_URL,
  FeatureFlagConstants,
  hanuManMovieId
} from '../../utils/constants/utilConstants';
import { breadCrumbFilter } from './breadCrumbFilterUtils';
import {
  getIndianLangObjects,
  getInternationalLangObjects,
  getXDefaultLangURL
} from './hrefLangHelpers';
import OGTags from './OGTags/OGTags';
import {
  breadcrumbSchema,
  personSchema,
  schemaDataManupulation,
  storiesItemListSchema
} from './SchemaDataCreation';
import { BreadCrumDataType, PageMeta, SeoMetaObject } from './SEOMetaTypes';
import { getCanonicalLink } from './SEOMetaUtils';
import { filterPathname } from './SEOURLUtils';
import { getFeatureFlagConfig } from 'src/utils/RemoteConfig';

export interface SEOMetaProps {
  isConsumption?: boolean;
  pageMeta?: PageMeta;
  tag?: string;
  liteData?: ContentLiteData | TvShowRelatedContent;
  videoSchemaData?: ContentLiteData;
  movieSchemaData?: ContentLiteData;
  apiData?: ContentLiteData | TvShowRelatedContent | Maybe<Maybe<Episode>[]>; // have to get this any removed later, currently putting is for build
  tvSeriesSchemaData?: ContentLiteData;
  tvSeriesSchemaShowDetailData?: ContentLiteData | TvShowRelatedContent;
  breadcrumbs?: BreadCrumDataType[];
  seoMetaObj?: SeoMetaObject;
  searchSchemaData?: boolean;
  trailerData?: ContentLiteData;
  licenseData?: DynamicObject;
  itemListSchema?: ContentLiteData | TvShowRelatedContent | CelebsMetaData;
  webPageSchemaData?: ContentLiteData;
  tableSchema?: boolean;
  tvEpisodeSchemaData?: DynamicObject;
  organizationSchemaData?: boolean;
  itemListCollectionData?: DynamicObject;
  channelVideoSchemaData?: DynamicObject;
  nowdata?: DynamicObject;
  offerDetails?: DynamicObject;
  mediaObject?: DynamicObject;
  clipSchemaData?: DynamicObject;
  pageName?: string;
  faq?: DynamicObject;
  railData?: DynamicObject | Maybe<Maybe<Episode>[]>;
  seasonIndex?: DynamicObject | number;
  latestData?: DynamicObject;
  header?: string;
  consumptionData?: ContentLiteData | TvShowRelatedContent;
  popularData?: DynamicObject;
  keywords?: string;
  canonicalUrl?: string;
  person?: DynamicObject;
}

//TODO:  liteData only requires id, tags. Better to extract that object instead of passing complete obj. Will also help to maintain test cases.
const SEOMeta = ({
  isConsumption = false,
  pageMeta,
  tag,
  liteData,
  videoSchemaData,
  movieSchemaData,
  apiData,
  tvSeriesSchemaData,
  tvSeriesSchemaShowDetailData,
  breadcrumbs,
  seoMetaObj,
  // televisionChannelSchemaData,
  searchSchemaData,
  trailerData,
  licenseData,
  itemListSchema,
  webPageSchemaData,
  tableSchema,
  tvEpisodeSchemaData,
  organizationSchemaData,
  itemListCollectionData,
  channelVideoSchemaData,
  nowdata,
  offerDetails,
  mediaObject,
  clipSchemaData,
  pageName,
  faq,
  railData,
  seasonIndex,
  latestData,
  header,
  consumptionData,
  popularData,
  keywords,
  canonicalUrl,
  person
}: // tvSeriesSchemaEpisodeData
SEOMetaProps) => {
  //constants
  const router = useRouter();

  const { usersLocationData, translations, usersPreferenceData, remoteConfig } =
    useAppStore();
  const ConsumptionPageTranslations = translations?.consumption;
  const { IN } = CountryCode;
  const countryCode = usersLocationData?.country_code || IN;
  const settings = usersPreferenceData || getLocalStorage(SETTINGS);
  const displayLanguage =
    getCookie(DISPLAY_LANGUAGE_COOKIE) ||
    settings?.display_language ||
    DisplayLanguage.ENGLISH;

  let routerPath = router.asPath;
  if (routerPath.startsWith('/global')) {
    routerPath = routerPath.replace('/global', '');
  }
  const isIndia = countryCode === IN;

  const ogTags = createOgTags(
    liteData || apiData,
    routerPath,
    isConsumption,
    displayLanguage,
    countryCode
  );
  const z5Strings = ConsumptionPageTranslations;
  const { ogImage, ogUrl } = ogTags;

  //Note: For some specific contents, in lower environments, values might not be present.
  const { title = '', description = '' } = pageMeta || {};

  const breadCrumbArray =
    breadcrumbs && Array.isArray(breadcrumbs)
      ? breadCrumbFilter(
          breadcrumbs,
          countryCode,
          displayLanguage,
          ConsumptionPageTranslations
        )
      : null;

  const isIndianLanguages = seoMetaObj ? seoMetaObj.isIndia : true;
  const isGlobalLanguages = seoMetaObj ? seoMetaObj.isGlobal : true;

  //undefined for movies page need to check for the rangbaaz
  const collectionPageId = '';

  const { id = '' } = (apiData as ContentLiteData) || {};

  const pathNameArray = routerPath ? filterPathname(routerPath).split('/') : [];

  //used for ladies and gentleman episode index
  const episodeIndex = id && routerPath ? getEpisodeIndex(id, countryCode) : -1;

  //used for RRR pageMeta
  const assetTitle =
    id &&
    assetContentMapping[id as keyof typeof assetContentMapping] &&
    assetContentMapping[id as keyof typeof assetContentMapping].title;
  const assetDescription =
    id &&
    assetContentMapping[id as keyof typeof assetContentMapping] &&
    assetContentMapping[id as keyof typeof assetContentMapping].description;

  const { payload } = getFeatureFlagConfig(
    remoteConfig,
    FeatureFlagConstants.CONFIG_SEO_URL
  );

  const { useCanonicalUrl = false } = payload || {};

  //Assuming: OG tags are there
  const canonicalURL =
    canonicalUrl && useCanonicalUrl
      ? `${BASE_URL}${canonicalUrl}`
      : getCanonicalLink(id, collectionPageId, ogUrl, countryCode);

  const isWantedGlobal = ogUrl?.includes(WANTED_CONTENT_ID) && isIndia;
  const isWriterPadhmaBushan =
    ogUrl?.includes(WRITER_PADMABUSHAN_CONTENT_ID) && isIndia;
  const isRRRIndia = ogUrl?.includes(RRR_CONTENT_ID) && isIndia;
  const isKisiKaBhaiKisiKiJaanCeleb =
    ogUrl?.includes(KISI_KA_BHAI_KISI_KI_JAAN_CONTENT_ID) && isIndia;
  const isGadar2India = ogUrl && ogUrl.includes(gadar2MovieID);
  const isSirfEkBandaaKaafiHaiIndia =
    ogUrl?.includes(SIRF_EK_BANDAA_KAAFI_HAI_CONTENT_ID) && isIndia;
  const isGhoomer = ogUrl && ogUrl.includes(ghoomerMovieId);
  const isKadakSingh = ogUrl && ogUrl.includes(kadakSinghMovieId);
  const isTejasMovie = ogUrl && ogUrl.includes(tejasMovieId);
  const isSamBahadurMovie = ogUrl && ogUrl.includes(samBahadurMovieId);
  const isKhichdi2Movie = ogUrl && ogUrl.includes(Khichdi2MovieId);
  const isKeralaStoryMovie = ogUrl && ogUrl.includes(theKeralaStoryMovieId);
  const ismainAtalHoonMovie = ogUrl && ogUrl.includes(mainAtalHoonMovieId);
  const isHanuManMovie = ogUrl && ogUrl.includes(hanuManMovieId);

  return (
    <>
      <Head data-testid="seoHeadTitle">
        <title>{assetTitle || title || ''}</title>

        <meta
          name="description"
          content={assetDescription || description || ''}
        />
        {keywords && <meta name="keywords" content={keywords} />}

        {canonicalURL ? <link rel="canonical" href={canonicalURL} /> : ''}

        {/** x-default for india and global */}
        {ogUrl &&
          id &&
          getXDefaultLangURL(
            id,
            isGlobalLanguages,
            isIndianLanguages,
            routerPath,
            countryCode,
            episodeIndex
          ).map((link: string) => (
            <link
              rel="alternate"
              hrefLang={'x-default'}
              key={`x-default-${link}`}
              href={link}
            />
          ))}

        {/*International alternate hreflang links  */}
        {ogUrl &&
          id &&
          isGlobalLanguages &&
          getInternationalLangObjects(
            id,
            ogUrl,
            countryCode,
            routerPath,
            isGlobalLanguages,
            !!apiData
          ).map(({ href, hrefLang }) => (
            <link
              rel="alternate"
              key={`int-${href}-${hrefLang}`}
              hrefLang={hrefLang}
              href={href}
            />
          ))}

        {/* Indian alternative href link */}
        {ogUrl &&
          id &&
          isIndianLanguages &&
          getIndianLangObjects(id, countryCode, routerPath, ogUrl).map(
            ({ href, hrefLang }) => (
              <link
                rel="alternate"
                key={`in-${href}`}
                hrefLang={hrefLang}
                href={href}
              />
            )
          )}

        {/* schema objects starts here */}
        {videoSchemaData ? (
          <script
            id="videoObject"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData,
                showDetail: videoSchemaData,
                location: routerPath,
                type: 'VideoObject',
                z5Strings,
                translation: displayLanguage,
                tag,
                ogImage,
                url: ogUrl,
                countryCode
              })}`
            }}></script>
        ) : (
          ''
        )}

        {movieSchemaData ? (
          <script
            id="movie"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData,
                trailerData: movieSchemaData,
                //not used inside
                location: routerPath,
                type: 'Movie',
                z5Strings,
                translation: displayLanguage,
                tag,
                ogImage,
                url: ogUrl,
                countryCode,
                licenseData: licenseData
              })}`
            }}></script>
        ) : (
          ''
        )}

        {tvSeriesSchemaData &&
        pathNameArray.length > 1 &&
        pathNameArray[1] === 'web-series' ? (
          <script
            id="TVSeries"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: tvSeriesSchemaData,
                location: routerPath,
                type: 'TVSeries',
                z5Strings,
                translation: displayLanguage,
                tag,
                ogImage,
                url: ogUrl,
                countryCode,
                licenseData: licenseData
              })}`
            }}></script>
        ) : (
          ''
        )}

        {tvEpisodeSchemaData &&
        pathNameArray.length > 1 &&
        (pathNameArray[1] === 'tv-shows' ||
          pathNameArray[1] === 'kids' ||
          (pathNameArray[1] === 'web-series' && pathNameArray.length === 7)) ? (
          <script
            id="TVEpisodes"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: tvEpisodeSchemaData,
                showDetailData: tvSeriesSchemaShowDetailData,
                location: routerPath,
                type: 'TVEpisodes',
                z5Strings,
                translation: displayLanguage,
                tag,
                ogImage,
                url: ogUrl,
                countryCode,
                licenseData: licenseData
              })}`
            }}></script>
        ) : (
          ''
        )}

        {organizationSchemaData ? (
          <script
            id="organization"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: '',
                type: 'Organization'
              })}`
            }}></script>
        ) : (
          ''
        )}

        {itemListCollectionData ? (
          <script
            id="itemListCollection"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: apiData,
                translation: displayLanguage,
                type: 'ItemListCollection'
              })}`
            }}></script>
        ) : (
          ''
        )}

        {channelVideoSchemaData ? (
          <script
            id="videoObject"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: channelVideoSchemaData,
                nowData: nowdata,
                location: routerPath,
                type: 'ChannelVideoObject',
                url: ogUrl,
                ogImage
              })}`
            }}></script>
        ) : (
          ''
        )}

        {searchSchemaData ? (
          <script
            id="websiteSearch"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: '',
                type: 'websiteSearch'
              })}`
            }}></script>
        ) : (
          ''
        )}

        {webPageSchemaData && Object.keys(webPageSchemaData).length > 0 ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: webPageSchemaData,
                type: 'webPageSchema',
                url: ogUrl,
                title,
                description
              })}`
            }}></script>
        ) : (
          ''
        )}

        {/* BreadCrumSchema */}
        {breadcrumbs && breadcrumbs.length && breadCrumbArray ? (
          <script
            id="schema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${breadcrumbSchema(breadCrumbArray)}`
            }}></script>
        ) : (
          ''
        )}

        {/* PersonSchema */}
        {person && Object.keys(person).length > 0 ? (
          <script
            id="schema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${personSchema(person)}`
            }}></script>
        ) : (
          ''
        )}

        <script
          id="siteNavigation"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${schemaDataManupulation({
              apiData: '',
              location: routerPath,
              type: 'siteNavigationElement',
              countryCode
            })}`
          }}></script>

        {itemListSchema && railData && railData.length > 0 ? (
          <script
            id="itemList"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: itemListSchema,
                railData: railData,
                location: routerPath,
                type: 'itemList',
                seasonIndex: seasonIndex,
                seoData: popularData || ''
              })}`
            }}></script>
        ) : (
          ''
        )}

        {tableSchema &&
        ((latestData && latestData.length >= 3) || consumptionData) ? (
          <script
            id="table"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                type: 'Table',
                tableData: latestData,
                location: routerPath,
                listData: header
              })}`
            }}></script>
        ) : (
          ''
        )}

        {tableSchema && routerPath.includes('/celebs') ? (
          <script
            id="table"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                type: 'Table',
                tableData: latestData,
                location: routerPath,
                listData: header
              })}`
            }}></script>
        ) : (
          ''
        )}

        {itemListSchema && routerPath === '/movies/year' ? (
          <script
            id="itemList"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: itemListSchema,
                railData: railData,
                location: routerPath,
                type: 'itemList',
                seasonIndex: seasonIndex
              })}`
            }}></script>
        ) : (
          ''
        )}

        {itemListSchema && routerPath.includes('/celebs') ? (
          <script
            id="itemList"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: itemListSchema,
                location: routerPath,
                type: 'itemList',
                railData: person
              })}`
            }}></script>
        ) : (
          ''
        )}

        {faq ? (
          <script
            id="faq"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: faq,
                type: 'faqList'
              })}`
            }}></script>
        ) : (
          ''
        )}

        {pageName === 'stories' ? (
          <script
            id="itemList"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${storiesItemListSchema()}`
            }}></script>
        ) : (
          ''
        )}

        {clipSchemaData && Object.keys(clipSchemaData).length ? (
          <script
            id="clip"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                apiData: clipSchemaData,
                pageMeta: pageMeta,
                location: routerPath,
                type: 'Clip',
                z5Strings,
                translation: displayLanguage,
                tag,
                url: ogUrl,
                countryCode
              })}`
            }}></script>
        ) : (
          ''
        )}

        {mediaObject && offerDetails && Object.keys(offerDetails).length ? (
          <script
            id="schema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${schemaDataManupulation({
                pageMeta: pageMeta,
                apiData: offerDetails,
                type: 'MediaObject'
              })}`
            }}></script>
        ) : (
          ''
        )}
        {/* FAQ schema  */}
        {ogUrl &&
          countryCode &&
          (isWantedGlobal ||
            isRRRIndia ||
            isSirfEkBandaaKaafiHaiIndia ||
            isWriterPadhmaBushan ||
            isKisiKaBhaiKisiKiJaanCeleb ||
            isGadar2India ||
            isGhoomer ||
            isKadakSingh ||
            isTejasMovie ||
            isSamBahadurMovie ||
            isKhichdi2Movie ||
            isKeralaStoryMovie ||
            ismainAtalHoonMovie ||
            isHanuManMovie) && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: `${schemaDataManupulation({
                  type: 'QnA',
                  ogUrl
                })}`
              }}></script>
          )}
      </Head>
      <OGTags
        description={assetDescription || description}
        title={title}
        assetTitle={assetTitle}
        displayLanguage={displayLanguage}
        ogTags={ogTags}
      />
    </>
  );
};

export default SEOMeta;
